<template>
  <div class="directory-node">
    <div
      class="directory-item"
      :class="{
        'selected': selectedDir && selectedDir.id === dir.id,
        'is-root': isRootNode,
        'dir-item--editable': isDirContentEditable,
        'dir-item--readonly': !isDirContentEditable,
      }"
      v-draggable="isRootNode ? null : dir.id"
      v-droppable="handleDropIn"
      :data-dir-id="dir.id"
      @click.stop="handleSelect"
      @contextmenu.prevent="showContextMenu($event)"
    >
      <span class="tree-indent" :style="{ width: (level * 14 + 6) + 'px' }"></span>
      <span class="tree-expand-cell" @click.stop="handleExpandClick">
        <Icon
          v-if="hasChildren"
          :type="expanded ? 'ios-arrow-down' : 'ios-arrow-forward'"
          :size="16"
          class="expand-icon"
        />
        <span v-else class="expand-placeholder"></span>
      </span>
      <span class="dir-icon-wrap">
        <BaseIcon
          :icon="dirIconMeta.icon"
          :size="16"
          :color="isSelected ? '#fff' : dirIconMeta.color"
          class="dir-icon"
        />
        <BaseIcon
          v-if="dirIconMeta.refBadge"
          icon="lianjie"
          :size="10"
          class="dir-ref-badge"
        />
      </span>
      <span
        class="directory-name"
        :class="'directory-name--' + dirNameAccessLevel"
      >{{ dir.name }}</span>
      <BaseIcon
        v-if="showSystemBadge"
        icon="md-settings"
        :size="14"
        class="dir-badge-icon system-badge"
        title="系统目录，不可编辑属性"
      />
      <BaseIcon
        v-if="showExternalSharedBadge"
        icon="md-globe"
        :size="14"
        class="dir-badge-icon shared-badge"
        title="外部公开"
      />
      <BaseIcon
        v-else-if="showInternalPrivateBadge"
        icon="md-lock"
        :size="14"
        class="dir-badge-icon private-badge"
        title="内部非公开"
      />
      <span v-if="dir.data_type === 'rag'" class="data-type-badge rag-badge">RAG</span>
    </div>
    <div v-if="expanded && hasChildren" class="directory-children">
      <directory-node
        v-for="child in nodeChildren"
        :key="getDirectoryVueKey(child, { parentId: childRefParentId })"
        :dir="child"
        :selected-dir="selectedDir"
        :level="level + 1"
        :is-root="false"
        :ref-parent-id="childRefParentId"
        :all-dirs-flat="allDirsFlat"
        :expand-token="expandToken"
        :session="session"
        :knowledge-auth="knowledgeAuth"
        @on-select="$emit('on-select', $event)"
        @on-drop-in="$emit('on-drop-in', $event)"
        @on-drop-file="$emit('on-drop-file', $event)"
        @on-config="$emit('on-config', $event)"
        @on-context-action="$emit('on-context-action', $event)"
        @on-load-ref="$emit('on-load-ref', $event)"
      />
    </div>
  </div>
</template>

<script>
import BaseIcon from '@/components/base/Icon';
import {
  canCreateSubDirectory,
  canEditDirectory,
  getDirectoryIconMeta,
  getDirectoryVueKey,
  getDirectoryNameAccessLevel,
  getEffectiveIsPrivate,
  getEffectiveIsShared,
  getRefDirectoryTargetId,
  isRefDirectory,
  isSystemDirectory,
  resolveDirectoryContentRoot,
} from '@/pages/core/knowledge/knowledge-permissions';

export default {
  name: 'DirectoryNode',
  components: {BaseIcon},
  props: {
    dir: {
      type: Object,
      required: true,
    },
    selectedDir: {
      type: Object,
      default: null,
    },
    level: {
      type: Number,
      default: 0,
    },
    isRoot: {
      type: Boolean,
      default: false,
    },
    allDirsFlat: {
      type: Array,
      default: () => [],
    },
    expandToken: {
      type: Object,
      default: () => ({ version: 0, expanded: true }),
    },
    refParentId: {
      default: null,
    },
    session: {
      type: Object,
      default: null,
    },
    knowledgeAuth: {
      type: Object,
      default: () => ({ roles: [], groups: [] }),
    },
  },
  data() {
    return {
      expanded: true,
    };
  },
  computed: {
    nodeChildren() {
      return this.dir.children || this.dir.subs || [];
    },
    hasChildren() {
      if (this.nodeChildren.length > 0) return true;
      return isRefDirectory(this.dir) && !!getRefDirectoryTargetId(this.dir);
    },
    isRootNode() {
      if (this.dir && this.dir._focusRoot) return true;
      return this.isRoot && (this.dir.parent_id === null || this.dir.parent_id === undefined);
    },
    isSystemDir() {
      return isSystemDirectory(this.dir);
    },
    isSelected() {
      return this.selectedDir && this.selectedDir.id === this.dir.id;
    },
    dirIconMeta() {
      return getDirectoryIconMeta(this.dir, {
        isRoot: this.isRootNode,
        allDirectories: this.allDirsFlat,
      });
    },
    isDirContentEditable() {
      return canEditDirectory(
        this.session,
        this.dir,
        this.allDirsFlat,
        this.knowledgeAuth,
      );
    },
    dirNameAccessLevel() {
      return getDirectoryNameAccessLevel(
        this.session,
        this.dir,
        this.allDirsFlat,
        this.knowledgeAuth,
      );
    },
    showExternalSharedBadge() {
      return getEffectiveIsShared(this.dir, this.allDirsFlat);
    },
    showInternalPrivateBadge() {
      if (this.isSystemDir) return false;
      return getEffectiveIsPrivate(this.dir, this.allDirsFlat);
    },
    showSystemBadge() {
      return this.isSystemDir && this.isRootNode;
    },
    childRefParentId() {
      if (isRefDirectory(this.dir) || this.dir._refDisplay) {
        return this.dir.id;
      }
      return this.refParentId;
    },
  },
  watch: {
    'expandToken.version'() {
      this.expanded = this.expandToken.expanded;
    },
    nodeChildren(val) {
      if (val && val.length && isRefDirectory(this.dir)) {
        this.expanded = true;
      }
    },
  },
  methods: {
    getDirectoryVueKey,
    handleExpandClick() {
      if (isRefDirectory(this.dir) && this.nodeChildren.length === 0) {
        this.$emit('on-load-ref', this.dir);
        this.expanded = true;
        return;
      }
      this.expanded = !this.expanded;
    },
    handleSelect() {
      this.$emit('on-select', this.dir);
    },
    showContextMenu(e) {
      const dir = this.dir;
      const isRoot = this.isRootNode;
      const isSystem = isSystemDirectory(dir);
      const contentDir = resolveDirectoryContentRoot(dir, this.allDirsFlat) || dir;
      const canEdit = canEditDirectory(
        this.session,
        contentDir,
        this.allDirsFlat,
        this.knowledgeAuth,
      );
      const canCreateSub = canCreateSubDirectory(
        this.session,
        contentDir,
        this.allDirsFlat,
        this.knowledgeAuth,
      );
      const items = [
        { label: '单独打开', onClick: () => {
          this.$emit('on-context-action', { action: 'open-alone', dir });
        }},
        { label: '作为文档打开', onClick: () => {
          this.$emit('on-context-action', { action: 'open-as-doc', dir });
        }},
      ];
      if (!canEdit && !canCreateSub) {
        this.$contextmenu({
          event: e,
          items,
          zIndex: 9999,
          minWidth: 120,
        });
        return;
      }
      if (canCreateSub) {
        items.push(
          { label: '新增子目录', onClick: () => {
            this.$emit('on-select', dir);
            this.$emit('on-context-action', { action: 'add', dir });
          }},
        );
      }
      if (canEdit && !isRoot && !isSystem) {
        items.push(
          { label: '编辑', onClick: () => {
            this.$emit('on-select', dir);
            this.$emit('on-context-action', { action: 'edit', dir });
          }},
          { label: '删除', onClick: () => {
            this.$emit('on-context-action', { action: 'remove', dir });
          }},
        );
      }
      this.$contextmenu({
        event: e,
        items,
        zIndex: 9999,
        minWidth: 120,
      });
    },
    handleDropIn(draggedId, targetData) {
      if (!draggedId || !targetData || !targetData.dirId) return;
      if (String(draggedId) === String(targetData.dirId)) return;
      this.$emit('on-drop-in', {
        draggedId,
        targetDirId: targetData.dirId,
      });
      this.$emit('on-drop-file', {
        draggedId,
        targetDirId: targetData.dirId,
      });
    },
  },
};
</script>

<style lang="less" scoped>
.directory-node {
  .directory-item {
    padding: 2px 6px;
    margin: 0;
    min-height: 24px;
    line-height: 22px;
    border-radius: 3px;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: all 0.2s;
    user-select: none;
    position: relative;

    &:hover {
      background: var(--border);
    }

    &.selected {
      background: var(--primary);
      color: #fff;

      .link-badge {
        background: rgba(255, 255, 255, 0.3);
      }
    }

    .tree-indent {
      flex-shrink: 0;
      display: inline-block;
    }

    .tree-expand-cell {
      width: 18px;
      height: 18px;
      margin-right: 2px;
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }

    .expand-icon {
      color: var(--subtext2);
      line-height: 1;
    }

    &.selected .expand-icon {
      color: rgba(255, 255, 255, 0.95);
    }

    .expand-placeholder {
      width: 16px;
      height: 16px;
      display: inline-block;
    }

    .dir-icon-wrap {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      margin-right: 5px;
      flex-shrink: 0;
    }

    .dir-ref-badge {
      position: absolute;
      right: -4px;
      bottom: -3px;
      background: var(--primary);
      color: #fff;
      border-radius: 50%;
      padding: 1px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
    }

    .directory-name {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-weight: 500;

      &--full {
        color: #389e0d;
      }

      &--authorized {
        color: #1890ff;
      }

      &--readonly {
        color: #000;
      }

      &--system {
        font-weight: 700;
        color: var(--primary);
      }
    }

    &.selected .directory-name {
      color: #fff !important;
    }

    .link-badge {
      font-size: 10px;
      padding: 2px 6px;
      background: var(--subtext3);
      color: #fff;
      border-radius: 2px;
      margin-left: 5px;
    }

    .remote-badge {
      background: #fa8c16;
    }

    .data-type-badge {
      font-size: 10px;
      padding: 2px 6px;
      background: #e6f7ff;
      color: #096dd9;
      border: 1px solid #91d5ff;
      border-radius: 2px;
      margin-left: 5px;
    }

    .rag-badge {
      background: #f9f0ff;
      color: #531dab;
      border-color: #d3adf7;
    }
    
    .dir-badge-icon {
      color: var(--subtext2);
      margin-left: 5px;
      flex-shrink: 0;

      &.shared-badge {
        color: #1890ff;
      }

      &.system-badge {
        color: var(--primary);
      }

      &.private-badge {
        color: var(--subtext3);
      }

      .selected & {
        color: rgba(255, 255, 255, 0.85);
      }
    }
  }

  .directory-children {
    margin-left: 0;
    margin-top: 0;
  }
}

.ugo-draggable {
  opacity: 0.5;
}

.ugo-droppable {
  background: var(--primary-light) !important;
  border: 2px dashed var(--primary) !important;
}
</style>
